/**
 * HTML 内容合并 Hook
 *
 * 功能：
 * - 安全地合并两个 HTML 内容
 * - 提取 quotedContent 的 body 标签中的实际 DOM 内容
 * - 将引文内容插入到主内容 body 标签的最末尾
 * - 做好容错处理，避免报错和插入错误
 */

/**
 * 安全地解析 HTML 字符串并提取 body 内容
 * @param html HTML 字符串
 * @returns body 标签内的 HTML 内容，如果解析失败则返回原字符串
 */
function extractBodyContent(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    // 使用 DOMParser 解析 HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // 检查是否有解析错误
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.warn('HTML parsing error, falling back to original content:', html);
      return html;
    }

    // 提取 body 内容
    const body = doc.body;
    if (body) {
      return body.innerHTML;
    }

    // 如果没有 body 标签，尝试查找是否有 body 内容
    // 有些 HTML 片段可能直接就是 body 内容
    return html;
  } catch (error) {
    console.error('Error extracting body content:', error);
    // 容错：如果解析失败，返回原字符串
    return html;
  }
}

/**
 * 安全地将内容插入到 HTML 的 body 末尾
 * @param mainHtml 主 HTML 内容
 * @param contentToAppend 要追加的内容（body 内的 HTML）
 * @returns 合并后的完整 HTML
 */
function insertContentToBodyEnd(mainHtml: string, contentToAppend: string): string {
  if (!mainHtml || typeof mainHtml !== 'string') {
    // 如果主内容为空，返回追加内容包装在 body 中
    return contentToAppend ? `<body>${contentToAppend}</body>` : '';
  }

  if (!contentToAppend || typeof contentToAppend !== 'string') {
    // 如果没有要追加的内容，返回主内容
    return mainHtml;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(mainHtml, 'text/html');

    // 检查是否有解析错误
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
      console.warn('Main HTML parsing error, using simple concatenation:', mainHtml);
      // 容错：如果解析失败，使用简单拼接
      return mainHtml + contentToAppend;
    }

    const body = doc.body;
    if (body) {
      // 将内容追加到 body 末尾
      body.insertAdjacentHTML('beforeend', contentToAppend);

      // 返回完整的 HTML 文档
      return doc.documentElement.outerHTML;
    }

    // 如果没有 body 标签，尝试创建完整的 HTML 结构
    // 检查是否已经有 html 标签
    if (doc.documentElement) {
      // 创建 body 标签并追加内容
      const newBody = doc.createElement('body');
      newBody.innerHTML = mainHtml;
      newBody.insertAdjacentHTML('beforeend', contentToAppend);
      doc.documentElement.appendChild(newBody);
      return doc.documentElement.outerHTML;
    }

    // 如果都没有，创建一个完整的 HTML 结构
    const htmlDoc = parser.parseFromString('<!DOCTYPE html><html><head></head><body></body></html>', 'text/html');
    if (htmlDoc.body) {
      htmlDoc.body.innerHTML = mainHtml;
      htmlDoc.body.insertAdjacentHTML('beforeend', contentToAppend);
      return htmlDoc.documentElement.outerHTML;
    }

    // 最后的容错：简单拼接
    return mainHtml + contentToAppend;
  } catch (error) {
    console.error('Error inserting content to body end:', error);
    // 容错：如果解析失败，使用简单拼接
    return mainHtml + contentToAppend;
  }
}

/**
 * 合并 HTML 内容
 * 提取 quotedContent 的 body 内容，插入到主内容的 body 末尾
 *
 * @param mainContent 主 HTML 内容（编辑器内容）
 * @param quotedContent 引文 HTML 内容
 * @returns 合并后的 HTML 内容
 */
export function mergeHtmlContent(mainContent: string, quotedContent: string): string {
  // 容错处理：如果参数不是字符串，转换为字符串或返回空字符串
  const main = typeof mainContent === 'string' ? mainContent : String(mainContent || '');
  const quoted = typeof quotedContent === 'string' ? quotedContent : String(quotedContent || '');

  // 如果引文内容为空，直接返回主内容
  if (!quoted || quoted.trim() === '') {
    return main;
  }

  // 如果主内容为空，返回引文内容（包装在 body 中）
  if (!main || main.trim() === '') {
    const quotedBodyContent = extractBodyContent(quoted);
    return quotedBodyContent ? `<body>${quotedBodyContent}</body>` : quoted;
  }

  try {
    // 提取引文内容的 body 内容
    const quotedBodyContent = extractBodyContent(quoted);

    // 将引文内容插入到主内容的 body 末尾
    const mergedContent = insertContentToBodyEnd(main, quotedBodyContent);

    return mergedContent;
  } catch (error) {
    console.error('Error merging HTML content:', error);
    // 容错：如果合并失败，使用简单拼接
    return main + quoted;
  }
}

/**
 * useMergeHtmlContent Hook
 *
 * 返回一个函数用于合并 HTML 内容
 *
 * @returns mergeHtmlContent 函数
 */
export function useMergeHtmlContent() {
  return mergeHtmlContent;
}
